import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

interface BreadcrumbItem {
  label: string;
  href?: string;
  isCurrentPage?: boolean;
}

interface DynamicBreadcrumbProps {
  customBreadcrumbs?: BreadcrumbItem[];
  className?: string;
}

/**
 * Route configurations for generating breadcrumbs
 */
const routeConfigs: Record<string, { label: string; parent?: string }> = {
  '/': { label: 'Home' },
  '/auctions': { label: 'Auctions', parent: '/' },
  '/artworks': { label: 'Artworks', parent: '/' },
  '/artists': { label: 'Artists', parent: '/' },
  '/blog': { label: 'Blog', parent: '/' },
  '/contact': { label: 'Contact', parent: '/' },
  '/contact-form': { label: 'Contact Form', parent: '/contact' },
  '/appraisal': { label: 'Art Appraisal', parent: '/' },
  '/consign': { label: 'Consignment', parent: '/' },
  '/live-broadcast': { label: 'Live Broadcast', parent: '/' },
  '/broadcast': { label: 'Broadcast', parent: '/' },
  '/login': { label: 'Login', parent: '/' },
  '/dashboard': { label: 'Dashboard', parent: '/' },
  '/profile': { label: 'Profile', parent: '/dashboard' },
  '/privacy': { label: 'Privacy Policy', parent: '/' },
  '/terms': { label: 'Terms of Service', parent: '/' },
  '/cookies': { label: 'Cookie Policy', parent: '/' },
  '/sitemap': { label: 'Sitemap', parent: '/' },
  '/admin': { label: 'Administration', parent: '/' },
};

/**
 * Generate breadcrumb path from current location
 */
const generateBreadcrumbPath = (pathname: string, routeConfigs: Record<string, { label: string; parent?: string }>): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [];
  
  // Handle dynamic routes
  if (pathname.includes('/artworks/') && pathname !== '/artworks') {
    breadcrumbs.push({ label: 'Home', href: '/' });
    breadcrumbs.push({ label: 'Artworks', href: '/artworks' });
    breadcrumbs.push({ label: 'Artwork Details', isCurrentPage: true });
    return breadcrumbs;
  }
  
  if (pathname.includes('/auctions/') && pathname !== '/auctions') {
    breadcrumbs.push({ label: 'Home', href: '/' });
    breadcrumbs.push({ label: 'Auctions', href: '/auctions' });
    breadcrumbs.push({ label: 'Auction Details', isCurrentPage: true });
    return breadcrumbs;
  }
  
  if (pathname.includes('/artists/') && pathname !== '/artists') {
    breadcrumbs.push({ label: 'Home', href: '/' });
    breadcrumbs.push({ label: 'Artists', href: '/artists' });
    breadcrumbs.push({ label: 'Artist Profile', isCurrentPage: true });
    return breadcrumbs;
  }
  
  if (pathname.includes('/blog/') && pathname !== '/blog') {
    breadcrumbs.push({ label: 'Home', href: '/' });
    breadcrumbs.push({ label: 'Blog', href: '/blog' });
    breadcrumbs.push({ label: 'Article', isCurrentPage: true });
    return breadcrumbs;
  }
  
  // Handle static routes
  const currentRoute = routeConfigs[pathname];
  if (!currentRoute) {
    breadcrumbs.push({ label: 'Home', href: '/' });
    breadcrumbs.push({ label: 'Page', isCurrentPage: true });
    return breadcrumbs;
  }
  
  // Build breadcrumb chain
  const buildChain = (path: string): void => {
    const route = routeConfigs[path];
    if (!route) return;
    
    if (route.parent && route.parent !== path) {
      buildChain(route.parent);
    }
    
    if (path === pathname) {
      breadcrumbs.push({ label: route.label, isCurrentPage: true });
    } else {
      breadcrumbs.push({ label: route.label, href: path });
    }
  };
  
  buildChain(pathname);
  return breadcrumbs;
};

/**
 * Generate Schema.org BreadcrumbList structured data
 */
const generateBreadcrumbSchema = (breadcrumbs: BreadcrumbItem[]): object => {
  const baseUrl = 'https://floridahighwaymenauction.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.label,
      "item": crumb.href ? `${baseUrl}${crumb.href}` : undefined
    })).filter(item => item.item) // Remove items without URLs (current page)
  };
};

/**
 * Dynamic Breadcrumb Component
 * Automatically generates breadcrumbs based on current route or accepts custom breadcrumbs
 */
const DynamicBreadcrumb: React.FC<DynamicBreadcrumbProps> = ({ 
  customBreadcrumbs,
  className = "mb-3"
}) => {
  const location = useLocation();
  
  const breadcrumbs = useMemo(() => {
    return customBreadcrumbs || generateBreadcrumbPath(location.pathname, routeConfigs);
  }, [location.pathname, customBreadcrumbs]);
  
  const breadcrumbSchema = useMemo(() => {
    return generateBreadcrumbSchema(breadcrumbs);
  }, [breadcrumbs]);
  
  // Don't show breadcrumbs on homepage unless custom breadcrumbs are provided
  if (location.pathname === '/' && !customBreadcrumbs) {
    return null;
  }
  
  return (
    <>
      {/* Schema.org structured data for SEO */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      
      {/* Enhanced Breadcrumb navigation */}
      <nav className={`${className} py-2`} aria-label="Breadcrumb">
        <div className="bg-white/5 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/10">
          <Breadcrumb>
            <BreadcrumbList className="text-white flex items-center gap-1 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <span key={`breadcrumb-${index}`} className="contents">
                  <BreadcrumbItem className="flex items-center">
                    {crumb.isCurrentPage ? (
                      <BreadcrumbPage className="text-white/90 font-medium text-sm">
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : crumb.href ? (
                      <BreadcrumbLink asChild>
                        <Link 
                          to={crumb.href} 
                          className="flex items-center gap-1 text-white/60 hover:text-white/90 transition-all duration-200 hover:bg-white/10 rounded px-2 py-1 text-sm font-light"
                        >
                          {index === 0 && <Home className="h-3.5 w-3.5" />}
                          {crumb.label}
                        </Link>
                      </BreadcrumbLink>
                    ) : (
                      <span className="text-white/60 text-sm font-light px-2 py-1">
                        {crumb.label}
                      </span>
                    )}
                  </BreadcrumbItem>
                  {index < breadcrumbs.length - 1 && (
                    <BreadcrumbSeparator className="text-white/30 mx-0.5">
                      <span className="text-xs">›</span>
                    </BreadcrumbSeparator>
                  )}
                </span>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </nav>
    </>
  );
};

export default DynamicBreadcrumb; 